import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom'
import { Home, Sparkles, Star, Wand2, User } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useLiffAuth } from './hooks/useLiffAuth'
import { useAppStore } from './store/useAppStore'
import HomePage from './pages/HomePage'
import DreamPage from './pages/DreamPage'
import LotteryPage from './pages/LotteryPage'
import HeatmapPage from './pages/HeatmapPage'
import HoroscopePage from './pages/HoroscopePage'
import PaymentPage from './pages/PaymentPage'
import StorePage from './pages/StorePage'
import ProfilePage from './pages/ProfilePage'
import TransactionsPage from './pages/TransactionsPage'
import TermsPage from './pages/TermsPage'
import CheckPage from './pages/CheckPage'
import TarotPage from './pages/TarotPage'
import AdminLayout from './pages/admin/AdminLayout'
import AdminDashboardPage from './pages/admin/DashboardPage'
import AdminUsersPage from './pages/admin/UsersPage'
import AdminTxPage from './pages/admin/TransactionsPage'
import AdminLogsPage from './pages/admin/LogsPage'
import AdminApiCostsPage from './pages/admin/ApiCostsPage'

function LoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-dvh gap-4"
         style={{ background: 'var(--color-bg)' }}>
      <div className="text-5xl animate-pulse">{'\u{1FAB6}'}</div>
      <p style={{ color: 'var(--color-text-muted)' }}>{'\u0E01\u0E33\u0E25\u0E31\u0E07\u0E42\u0E2B\u0E25\u0E14...'}</p>
    </div>
  )
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-dvh gap-4 p-6 text-center"
         style={{ background: 'var(--color-bg)' }}>
      <div className="text-5xl">{'\u26A0\uFE0F'}</div>
      <p style={{ color: 'var(--color-danger)' }}>{message}</p>
      <button onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-xl font-semibold btn-gold">
        {'\u0E25\u0E2D\u0E07\u0E43\u0E2B\u0E21\u0E48'}
      </button>
    </div>
  )
}

const HIDDEN_NAV = ['/payment', '/admin', '/terms', '/store']

function BottomNav() {
  const location = useLocation()
  if (HIDDEN_NAV.some(p => location.pathname.startsWith(p))) return null

  const navItems = [
    { to: '/',           icon: <Home size={20} />,     label: '\u0E2B\u0E19\u0E49\u0E32\u0E41\u0E23\u0E01' },
    { to: '/dream',      icon: <Sparkles size={20} />, label: '\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19' },
    { to: '/horoscope',  icon: <Star size={20} />,     label: '\u0E14\u0E39\u0E14\u0E27\u0E07\u0E1F\u0E23\u0E35' },
    { to: '/tarot',      icon: <Wand2 size={20} />,    label: '\u0E44\u0E1E\u0E48\u0E22\u0E34\u0E1B\u0E0B\u0E35' },
    { to: '/profile',    icon: <User size={20} />,     label: '\u0E42\u0E1B\u0E23\u0E44\u0E1F\u0E25\u0E4C' },
  ]

  return (
    <nav className="w-full max-w-[430px] mx-auto flex-shrink-0"
         style={{
           background: 'var(--color-surface)',
           borderTop: '1px solid var(--color-surface-3)',
           boxShadow: '0 -4px 16px rgba(0,0,0,0.04)',
           paddingBottom: 'env(safe-area-inset-bottom, 0px)',
         }}>
      <div className="flex items-center justify-around py-2">
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'}
                   className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-colors"
                   style={({ isActive }) => ({
                     color: isActive ? 'var(--color-gold-dark)' : 'var(--color-text-soft)',
                   })}>
            {item.icon}
            <span className="text-[10px] font-medium">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

function AppShell() {
  const location = useLocation()
  const hasNav = !HIDDEN_NAV.some(p => location.pathname.startsWith(p))
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0
  }, [location.pathname])

  return (
    <div className="flex flex-col min-h-dvh">
      <div ref={contentRef}
           className={`flex-1 overflow-y-auto overflow-x-hidden ${hasNav ? 'pb-[68px]' : ''}`}
           style={{ WebkitOverflowScrolling: 'touch' }}>
        <Routes>
          <Route path="/"          element={<HomePage />} />
          <Route path="/dream"     element={<DreamPage />} />
          <Route path="/lottery"   element={<LotteryPage />} />
          <Route path="/heatmap"   element={<HeatmapPage />} />
          <Route path="/horoscope" element={<HoroscopePage />} />
          <Route path="/payment"   element={<PaymentPage />} />
          <Route path="/store"     element={<StorePage />} />
          <Route path="/profile"   element={<ProfilePage />} />
          <Route path="/check"   element={<CheckPage />} />
          <Route path="/tarot"   element={<TarotPage />} />
          <Route path="/profile/transactions" element={<TransactionsPage />} />
          <Route path="/terms"     element={<TermsPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="transactions" element={<AdminTxPage />} />
            <Route path="logs" element={<AdminLogsPage />} />
            <Route path="api-costs" element={<AdminApiCostsPage />} />
          </Route>
        </Routes>
      </div>
      {hasNav && <BottomNav />}
    </div>
  )
}

export default function App() {
  const { ready, error } = useLiffAuth()
  const { theme } = useAppStore()

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  if (!ready) return <LoadingScreen />
  if (error) return <ErrorScreen message={error} />

  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  )
}
